export interface WidgetPublicConfig {
  title: string;
  agentName: string;
  primaryColor: string;
  logoUrl: string | null;
  position: 'left' | 'right';
  customCss: string | null;
  greetingMessage: string | null;
  welcomeMessage: string | null;
  autoOpenEnabled: boolean;
  autoOpenDelaySeconds: number | null;
  autoOpenDisabledOnMobile: boolean;
  autoOpenDelayOverrides: Record<string, number> | null;
  sessionPersistenceTtlSeconds: number;
}

// Thrown by sendMessage when the server reports the session can no longer be
// resumed (410 — TTL expired per #130, or 404 — unknown session). Distinct
// from a generic network/server error so callers can transparently recover
// by starting a new session instead of just showing an error (#131). poke
export class WidgetSessionExpiredError extends Error {
  constructor() {
    super('Widget session has expired');
    this.name = 'WidgetSessionExpiredError';
  }
}

export interface StartSessionResult {
  sessionId: string;
}

export interface SendMessageInput {
  sessionId: string;
  message: string;
  customerContext?: Record<string, unknown>;
}

export interface SendMessageResult {
  content: string;
}

export type WidgetClientEventType = 'widget_loaded' | 'widget_opened' | 'widget_closed';

export interface RecordEventInput {
  eventType: WidgetClientEventType;
  sessionId?: string;
  properties?: Record<string, unknown>;
}

export interface WidgetApiClient {
  getConfig(): Promise<WidgetPublicConfig>;
  startSession(customerContext?: Record<string, unknown>): Promise<StartSessionResult>;
  sendMessage(input: SendMessageInput): Promise<SendMessageResult>;
  endSession(sessionId: string): Promise<void>;
  recordEvent(input: RecordEventInput): Promise<void>;
}

export function createWidgetApiClient(origin: string, publicId: string): WidgetApiClient {
  const base = `${origin}/api/embed/${publicId}`;

  return {
    async getConfig() {
      const res = await fetch(`${base}/config`);
      if (!res.ok) throw new Error(`Widget config request failed: ${res.status}`);
      return (await res.json()) as WidgetPublicConfig;
    },

    async startSession(customerContext) {
      const res = await fetch(`${base}/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customerContext ? { customerContext } : {}),
      });
      if (!res.ok) throw new Error(`Widget session request failed: ${res.status}`);
      return (await res.json()) as StartSessionResult;
    },

    async sendMessage(input) {
      const res = await fetch(`${base}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (res.status === 410 || res.status === 404) {
        throw new WidgetSessionExpiredError();
      }
      if (!res.ok) throw new Error(`Widget message request failed: ${res.status}`);
      return (await res.json()) as SendMessageResult;
    },

    async endSession(sessionId) {
      await fetch(`${base}/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      });
    },

    async recordEvent(input) {
      await fetch(`${base}/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        keepalive: true,
      });
    },
  };
}
